import React from 'react'
import {
  BriefcaseIcon,
  BuildingIcon,
  CalendarIcon,
  DollarSignIcon,
  FileTextIcon,
  LinkIcon,
  MapPinIcon,
  MonitorIcon,
  StickyNoteIcon,
} from 'lucide-react'

import {
  type ApplicationStatus,
  type WorkMode,
} from '@/components/hooks/use-job-log'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'

export interface JobEntryFormData {
  date: string
  position: string
  company: string
  workMode: WorkMode
  location: string
  salary: string
  url: string
  status: ApplicationStatus
  notes: string
  resume: string
}

const defaultFormData: JobEntryFormData = {
  date: new Date().toISOString().split('T')[0],
  position: '',
  company: '',
  workMode: '',
  location: '',
  salary: '',
  url: '',
  status: 'applied',
  notes: '',
  resume: '',
}

const workModeOptions: { value: WorkMode; label: string }[] = [
  { value: 'remote', label: 'Remote' },
  { value: 'hybrid', label: 'Hybrid' },
  { value: 'onsite', label: 'On-site' },
]

const statusOptions: { value: ApplicationStatus; label: string }[] = [
  { value: 'applied', label: 'Applied' },
  { value: 'interviewing', label: 'Interviewing' },
  { value: 'offered', label: 'Offered' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'withdrawn', label: 'Withdrawn' },
]

interface JobEntryDialogProps {
  trigger?: React.ReactNode
  title?: string
  description?: string
  initialData?: Partial<JobEntryFormData>
  onSave: (data: JobEntryFormData) => void
  open?: boolean
  onOpenChange?: (open: boolean) => void
}

export const JobEntryDialog = (props: JobEntryDialogProps) => {
  const {
    trigger,
    title = 'Log Job Application',
    description = 'Record the details of your job application',
    initialData,
    onSave,
    open: controlledOpen,
    onOpenChange: controlledOnOpenChange,
  } = props

  const [internalOpen, setInternalOpen] = React.useState(false)
  const isControlled = controlledOpen !== undefined
  const open = isControlled ? controlledOpen : internalOpen
  const onOpenChange = isControlled ? controlledOnOpenChange : setInternalOpen

  const [formData, setFormData] = React.useState<JobEntryFormData>({
    ...defaultFormData,
    ...initialData,
  })

  React.useEffect(() => {
    if (open) {
      setFormData({
        ...defaultFormData,
        ...initialData,
      })
    }
  }, [open, initialData])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    onSave(formData)
    onOpenChange?.(false)
  }

  const updateField = <K extends keyof JobEntryFormData>(
    field: K,
    value: JobEntryFormData[K],
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {trigger && (
        <DialogTrigger render={(props) => <span {...props}>{trigger}</span>} />
      )}
      <DialogContent className="sm:max-w-lg">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
            <DialogDescription>{description}</DialogDescription>
          </DialogHeader>

          <div className="mt-6 grid gap-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="date">Date Applied</Label>
                <div className="relative">
                  <CalendarIcon className="absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    id="date"
                    type="date"
                    value={formData.date}
                    onChange={(e) => updateField('date', e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="status">Status</Label>
                <Select
                  value={formData.status}
                  onValueChange={(value) =>
                    updateField('status', value as ApplicationStatus)
                  }
                >
                  <SelectTrigger id="status" data-placeholder="Select status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {statusOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="position">Position</Label>
              <div className="relative">
                <BriefcaseIcon className="absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="position"
                  value={formData.position}
                  onChange={(e) => updateField('position', e.target.value)}
                  placeholder="e.g. Senior Software Engineer"
                  className="pl-10"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="company">Company</Label>
              <div className="relative">
                <BuildingIcon className="absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="company"
                  value={formData.company}
                  onChange={(e) => updateField('company', e.target.value)}
                  placeholder="e.g. Acme Inc."
                  className="pl-10"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="workMode">Work Mode</Label>
                <div className="relative">
                  <MonitorIcon className="absolute top-1/2 left-3 z-10 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Select
                    value={formData.workMode}
                    onValueChange={(value) =>
                      updateField('workMode', value as WorkMode)
                    }
                  >
                    <SelectTrigger
                      id="workMode"
                      className="pl-10"
                      data-placeholder="Select mode"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {workModeOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="location">Location</Label>
                <div className="relative">
                  <MapPinIcon className="absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    id="location"
                    value={formData.location}
                    onChange={(e) => updateField('location', e.target.value)}
                    placeholder="e.g. San Francisco, CA"
                    className="pl-10"
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="salary">Salary</Label>
                <div className="relative">
                  <DollarSignIcon className="absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    id="salary"
                    value={formData.salary}
                    onChange={(e) => updateField('salary', e.target.value)}
                    placeholder="e.g. $150k - $180k"
                    className="pl-10"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="url">Job URL</Label>
                <div className="relative">
                  <LinkIcon className="absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    id="url"
                    type="url"
                    value={formData.url}
                    onChange={(e) => updateField('url', e.target.value)}
                    placeholder="https://..."
                    className="pl-10"
                  />
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="notes">Notes</Label>
              <div className="relative">
                <StickyNoteIcon className="absolute top-3 left-3 h-4 w-4 text-muted-foreground" />
                <Textarea
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => updateField('notes', e.target.value)}
                  placeholder="Any additional notes about this application..."
                  className="min-h-20 pl-10"
                />
              </div>
            </div>

            {formData.resume && (
              <div className="flex items-center gap-2 rounded-md border bg-muted/50 p-3">
                <FileTextIcon className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">
                  Tailored resume attached (
                  {Math.round(formData.resume.length / 1024)}KB)
                </span>
              </div>
            )}
          </div>

          <DialogFooter className="mt-6">
            <DialogClose render={<Button type="button" variant="outline" />}>
              Cancel
            </DialogClose>
            <Button type="submit">Save Application</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
